import { motion } from "framer-motion";
import { Container } from "react-bootstrap";

export default function WeeklySaturdayMarket() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" as const },
    },
  };

  const pulseVariants = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut" as const,
      },
    },
  };

  return (
    <div
      style={{
        background:
          "linear-gradient(135deg, #0a8c8c 0%, #14676c 50%, #1B5A7A 100%)",
        padding: "clamp(40px, 8vw, 60px) 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Background Elements */}
      <div
        style={{
          position: "absolute",
          top: "-50px",
          right: "-50px",
          width: "clamp(200px, 30vw, 300px)",
          height: "clamp(200px, 30vw, 300px)",
          borderRadius: "50%",
          background: "rgba(38, 198, 218, 0.1)",
          filter: "blur(60px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-80px",
          left: "-80px",
          width: "clamp(250px, 35vw, 400px)",
          height: "clamp(250px, 35vw, 400px)",
          borderRadius: "50%",
          background: "rgba(27, 90, 122, 0.15)",
          filter: "blur(80px)",
        }}
      />

      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          style={{
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Badge */}
          <motion.div
            variants={itemVariants}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "clamp(6px, 1.5vw, 8px)",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              padding: "clamp(8px, 2vw, 10px) clamp(16px, 4vw, 24px)",
              borderRadius: "999px",
              marginBottom: "clamp(16px, 3vw, 20px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <span style={{ fontSize: "clamp(0.9rem, 2vw, 1.1rem)" }}>🌟</span>
            <span
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "clamp(0.75rem, 1.8vw, 0.95rem)",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Year-Round Community Market
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h2
            variants={itemVariants}
            style={{
              fontSize: "clamp(2rem, 6vw, 3rem)",
              fontWeight: 900,
              color: "#fff",
              marginBottom: "clamp(16px, 3vw, 20px)",
              fontFamily: "'Montserrat', 'Segoe UI', Arial, sans-serif",
              letterSpacing: "-1px",
              textShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              lineHeight: 1.2,
            }}
          >
            Every Saturday
          </motion.h2>

          {/* Time Display */}
          <motion.div
            variants={pulseVariants}
            initial="initial"
            animate="animate"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "clamp(12px, 2.5vw, 16px)",
              background: "rgba(255, 255, 255, 0.95)",
              padding: "clamp(18px, 4vw, 24px) clamp(28px, 6vw, 40px)",
              borderRadius: "20px",
              marginBottom: "clamp(24px, 4vw, 32px)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            }}
          >
            <span style={{ fontSize: "clamp(1.8rem, 4vw, 2.5rem)" }}>🕘</span>
            <span
              style={{
                fontSize: "clamp(1.3rem, 4vw, 2rem)",
                fontWeight: 800,
                background: "linear-gradient(90deg, #1B5A7A, #26c6da)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontFamily: "'Montserrat', 'Segoe UI', Arial, sans-serif",
                letterSpacing: "-0.5px",
              }}
            >
              9:00 AM – 4:00 PM
            </span>
          </motion.div>

          {/* Feature Grid */}
          <motion.div
            variants={itemVariants}
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
              gap: "clamp(16px, 3vw, 20px)",
              marginTop: "clamp(32px, 5vw, 40px)",
              maxWidth: "1000px",
              margin: "clamp(32px, 5vw, 40px) auto 0",
              padding: "0 clamp(16px, 3vw, 0px)",
            }}
          >
            {[
              { icon: "🥕", title: "Fresh Produce", desc: "Local & Organic" },
              { icon: "🎨", title: "Artisan Goods", desc: "Handcrafted Items" },
              { icon: "🎵", title: "Live Events", desc: "Music & Workshops" },
              { icon: "🤝", title: "Community", desc: "Meet Your Neighbors" },
            ].map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(10px)",
                  padding: "clamp(20px, 4vw, 24px) clamp(16px, 3vw, 20px)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "default",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(2rem, 4vw, 2.5rem)",
                    marginBottom: "clamp(8px, 2vw, 12px)",
                  }}
                >
                  {feature.icon}
                </div>
                <h4
                  style={{
                    fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: "clamp(4px, 1vw, 6px)",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {feature.title}
                </h4>
                <p
                  style={{
                    fontSize: "clamp(0.85rem, 1.8vw, 0.95rem)",
                    color: "rgba(255, 255, 255, 0.85)",
                    margin: 0,
                  }}
                >
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Call to Action */}
          <motion.div
            variants={itemVariants}
            style={{
              marginTop: "clamp(32px, 5vw, 40px)",
              padding: "0 16px",
            }}
          >
            <p
              style={{
                fontSize: "clamp(1rem, 2.5vw, 1.3rem)",
                color: "#fff",
                fontWeight: 600,
                marginBottom: "0",
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
                lineHeight: 1.4,
              }}
            >
              Rain or Shine ☀️ We're Here for You!
            </p>
          </motion.div>
        </motion.div>
      </Container>

      {/* Responsive Styles */}
      <style>
        {`
          @media (hover: hover) and (pointer: fine) {
            .feature-card:hover {
              box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
            }
          }

          @media (max-width: 768px) {
            .container {
              padding-left: 20px;
              padding-right: 20px;
            }
          }

          @media (max-width: 480px) {
            .container {
              padding-left: 16px;
              padding-right: 16px;
            }
          }
        `}
      </style>
    </div>
  );
}
